import { UserCheck, ArrowRight } from "lucide-react";
import { track } from "../lib/api";
import { LANDC_PARTNER_URL, BROKER_LINK_REL, brokerGaSource } from "../lib/broker";

/* Broker support CTA — L&C signed-off copy, reproduced VERBATIM. The card links
   directly to the approved L&C partner URL (external, new tab). MortgagePath's
   OWN commission disclosure is rendered as a separate block directly beneath the
   CTA — never merged into the L&C wording and never hidden. */
const TEAL_BG = "#E9F4F9";
const TEAL_BORDER = "#0E7490";
const TEAL_BUTTON = "#0E7490";
const TEAL_BUTTON_HOVER = "#155E75";
const ICON_BADGE_BG = "#CFE8F2";

const buttonStyle = { background: TEAL_BUTTON, color: "#FFFFFF" };
const onButtonEnter = (e) => { e.currentTarget.style.background = TEAL_BUTTON_HOVER; };
const onButtonLeave = (e) => { e.currentTarget.style.background = TEAL_BUTTON; };

/* ---------- L&C signed-off copy — VERBATIM (do not paraphrase / shorten) ---------- */
const Eyebrow = () => (
  <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: TEAL_BUTTON }}>
    Mortgage support
  </div>
);

const Headline = ({ small = false }) => (
  <h3 className={small ? "font-sora text-base font-semibold text-[#0B1220] mt-1" : "font-sora text-xl font-semibold text-[#0B1220] mt-1"}>
    Expert mortgage advice that won&apos;t cost you a penny
  </h3>
);

const Body = () => (
  <p className="mt-2 text-sm text-[#475569] leading-relaxed">
    Whether you&apos;re buying your first home, moving house or looking for a new mortgage deal, L&amp;C can help.
    Get expert mortgage advice, compare deals from across the market and receive support every step of the way.
  </p>
);

/* Supporting text sits directly beneath the button. */
const SupportingText = () => (
  <div className="mt-2 text-xs text-[#475569]">No obligation. Fee free advice.</div>
);

/* Repossession disclaimer — clearly visible and legible (not tiny, not hidden). */
const RepossessionDisclaimer = () => (
  <p className="mt-3 text-xs text-[#475569] leading-relaxed font-medium">
    Your home or property may be repossessed if you do not keep up repayments on your mortgage.
  </p>
);

/* MortgagePath commission disclosure — VERBATIM, kept visually separate from the
   L&C block, positioned directly beneath the CTA, smaller but clearly legible. */
const CommissionDisclosure = () => (
  <p className="mt-3 text-[11px] text-[#64748B] leading-relaxed" data-testid="broker-commission-disclosure">
    <span className="font-semibold text-[#475569]">MortgagePath commission disclosure:</span>{" "}
    If you complete a mortgage through L&amp;C after using our link, MortgagePath receives a commission from L&amp;C.
    The amount is calculated as a percentage of the commission L&amp;C receives from the lender. This does not increase
    what you pay.
  </p>
);

const IconBadge = ({ small = false }) => (
  <span
    className={`${small ? "w-8 h-8 rounded-lg" : "mt-0.5 w-10 h-10 rounded-xl"} grid place-items-center shrink-0`}
    style={{ background: ICON_BADGE_BG, color: TEAL_BUTTON }}
  >
    <UserCheck className={small ? "w-4 h-4" : "w-5 h-5"} />
  </span>
);

const BrokerCTA = ({ source = "direct", variant = "boxed", className = "" }) => {
  const onCtaClick = () => {
    // Single event per physical click. Tracking payload is intentionally
    // unchanged (preserves existing broker_click attribution & analytics).
    track("broker_click", {
      source: brokerGaSource(source),
      cta_location: source,
      cta_text: "Speak with a broker",
      partner: "L&C",
      destination: LANDC_PARTNER_URL,
      destination_type: "external",
    });
  };

  // Fully clickable card variant (guides / tool next-steps grids).
  if (variant === "clickable-card") {
    return (
      <div className={className}>
        <a
          href={LANDC_PARTNER_URL}
          target="_blank"
          rel={BROKER_LINK_REL}
          onClick={onCtaClick}
          className="block group rounded-2xl border p-5 shadow-sm hover:shadow-md transition-all"
          style={{ background: TEAL_BG, borderColor: TEAL_BORDER }}
          data-testid={`broker-cta-card-${source}`}
        >
          <div className="flex items-start gap-4">
            <IconBadge />
            <div className="flex-1">
              <Eyebrow />
              <Headline />
              <Body />
              <span
                className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm"
                style={buttonStyle}
                onMouseEnter={onButtonEnter}
                onMouseLeave={onButtonLeave}
              >
                Get mortgage help <ArrowRight className="w-4 h-4" />
              </span>
              <SupportingText />
              <RepossessionDisclaimer />
            </div>
          </div>
        </a>
        <CommissionDisclosure />
      </div>
    );
  }

  // Subtle variant — toned down for dense areas.
  if (variant === "subtle") {
    return (
      <div className={className}>
        <div
          className="rounded-2xl border p-5"
          style={{ background: "#F1F8FB", borderColor: "#9CCFE0" }}
          data-testid={`broker-cta-${source}`}
        >
          <div className="flex items-start gap-3">
            <IconBadge small />
            <div className="flex-1">
              <Eyebrow />
              <Headline small />
              <Body />
              <a
                href={LANDC_PARTNER_URL}
                target="_blank"
                rel={BROKER_LINK_REL}
                onClick={onCtaClick}
                className="mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shadow-sm transition-colors"
                style={buttonStyle}
                onMouseEnter={onButtonEnter}
                onMouseLeave={onButtonLeave}
                data-testid={`broker-cta-btn-${source}`}
              >
                Get mortgage help <ArrowRight className="w-4 h-4" />
              </a>
              <SupportingText />
              <RepossessionDisclaimer />
            </div>
          </div>
        </div>
        <CommissionDisclosure />
      </div>
    );
  }

  // Default boxed CTA — non-clickable card with a button.
  return (
    <div className={className}>
      <div
        className="rounded-2xl border p-6 sm:p-7 shadow-sm"
        style={{ background: TEAL_BG, borderColor: TEAL_BORDER }}
        data-testid={`broker-cta-${source}`}
      >
        <div className="flex items-start gap-4">
          <IconBadge />
          <div className="flex-1">
            <Eyebrow />
            <Headline />
            <Body />
            <a
              href={LANDC_PARTNER_URL}
              target="_blank"
              rel={BROKER_LINK_REL}
              onClick={onCtaClick}
              className="mt-4 inline-flex items-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold shadow-md transition-colors"
              style={buttonStyle}
              onMouseEnter={onButtonEnter}
              onMouseLeave={onButtonLeave}
              data-testid={`broker-cta-btn-${source}`}
            >
              Get mortgage help <ArrowRight className="w-4 h-4" />
            </a>
            <SupportingText />
            <RepossessionDisclaimer />
          </div>
        </div>
      </div>
      <CommissionDisclosure />
    </div>
  );
};

export default BrokerCTA;
